import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  Card, CardHeader, CardTitle, CardContent,
  Button, Input, Modal, ModalFooter, Badge, Alert, AlertDescription,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty,
  LastUpdated, EarningsTrendChart,
} from '../components/ui';
import { formatCurrency, formatDate, formatNumber } from '../lib/utils';
import { 
  Plus, Trash2, Settings, Save, Database, AlertCircle, CheckCircle, 
  Unplug, ChevronLeft, ChevronRight, LogIn, ExternalLink, Link as LinkIcon, ShieldCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { SkeletonPublisherPlatform } from '../components/ui/Skeleton';

const getMonthLabel = (date) => date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const getMonthRange = (year, month) => ({
  start: new Date(year, month, 1).toISOString().split('T')[0],
  end: new Date(year, month + 1, 0).toISOString().split('T')[0]
});

export default function AdSense() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchingDaily, setFetchingDaily] = useState(false);
  const [fetchingUrls, setFetchingUrls] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [monthOffset, setMonthOffset] = useState(0);
  
  const [showSettings, setShowSettings] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [revenueShare, setRevenueShare] = useState(68);
  const [manualForm, setManualForm] = useState({ 
    date: new Date().toISOString().split('T')[0], 
    gross_revenue: '', 
    page_views: '', 
    clicks: '' 
  });

  // Track visibility and loading state
  const lastVisibleTime = useRef(Date.now());
  const isLoadingRef = useRef(false);

  // Handle visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const timeSinceHidden = now - lastVisibleTime.current;
        
        // If we've been hidden for more than 30 seconds, reload silently
        if (timeSinceHidden > 30000 && !isLoadingRef.current) {
          setFetchingDaily(false);
          setFetchingUrls(false);
          setConnecting(false);
          
          setTimeout(() => {
            if (!isLoadingRef.current) {
              loadDataSilent();
            }
          }, 500);
        }
        
        lastVisibleTime.current = now;
      } else {
        lastVisibleTime.current = Date.now();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const getViewMonth = useCallback(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth() - monthOffset, 1);
  }, [monthOffset]);

  // Silent reload (no spinner)
  const loadDataSilent = useCallback(async () => {
    if (!user || isLoadingRef.current) return;
    
    try {
      const { data: s, error: sErr } = await supabase
        .from('adsense_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (!sErr?.message?.includes('AbortError') && !sErr?.message?.includes('Lock')) {
        if (s) {
          setSettings(s);
          setRevenueShare(s.revenue_share || 68);
        }
      }

      const vm = getViewMonth();
      const { start, end } = getMonthRange(vm.getFullYear(), vm.getMonth());
      
      const { data: daily, error: dErr } = await supabase
        .from('adsense_daily')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', start)
        .lte('date', end)
        .order('date', { ascending: false });
      
      if (!dErr?.message?.includes('AbortError') && !dErr?.message?.includes('Lock') && daily) {
        setDailyData(daily);
      }
    } catch (e) {
      // Silently ignore abort/lock errors
      if (!e.message?.includes('AbortError') && !e.message?.includes('Lock')) {
        console.error('Silent load error:', e);
      }
    }
  }, [user, getViewMonth]);

  const loadData = useCallback(async () => {
    if (!user) return;
    
    isLoadingRef.current = true;
    setLoading(true);
    
    try {
      const { data: s, error: sErr } = await supabase
        .from('adsense_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      // Ignore abort/lock errors
      if (sErr && !sErr.message?.includes('AbortError') && !sErr.message?.includes('Lock')) {
        console.error('Settings error:', sErr);
      }
      
      setSettings(s);
      if (s) setRevenueShare(s.revenue_share || 68);

      const vm = getViewMonth();
      const { start, end } = getMonthRange(vm.getFullYear(), vm.getMonth());
      
      const { data: daily, error: dErr } = await supabase
        .from('adsense_daily')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', start)
        .lte('date', end)
        .order('date', { ascending: false });
      
      if (dErr && !dErr.message?.includes('AbortError') && !dErr.message?.includes('Lock')) {
        console.error('Daily error:', dErr);
      }
      setDailyData(daily || []);
    } catch (e) { 
      // Don't show error for abort/lock errors
      if (!e.message?.includes('AbortError') && !e.message?.includes('Lock')) {
        console.error(e);
        toast.error('Failed to load data');
      }
    }
    
    setLoading(false);
    isLoadingRef.current = false;
  }, [user, getViewMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  // Handle OAuth callback
  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    
    if (error) {
      toast.error(`OAuth error: ${error}`);
      setSearchParams({});
      return;
    }
    
    if (code && user) {
      if (state && state !== user.id) {
        toast.error('Invalid OAuth state');
        setSearchParams({});
        return;
      }
      
      handleOAuthCallback(code);
      setSearchParams({});
    }
  }, [searchParams, user]);

  const handleOAuthCallback = async (code) => {
    setConnecting(true);
    const tid = toast.loading('Connecting to AdSense...');
    
    try {
      const res = await fetch('/api/adsense', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'callback', code, userId: user.id })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(`Connected to ${data.accountName}!`, { id: tid });
        
        // Update onboarding progress
        await supabase
          .from('onboarding_progress')
          .update({ adsense_connected: true, updated_at: new Date().toISOString() })
          .eq('user_id', user.id);
        
        loadData();
      } else {
        toast.error(data.error || 'Connection failed', { id: tid });
        console.log('OAuth callback error:', data);
      }
    } catch (e) {
      toast.error(e.message, { id: tid });
    }
    setConnecting(false);
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await fetch('/api/adsense', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getAuthUrl', userId: user.id })
      });
      
      const data = await res.json();
      
      if (data.success && data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        toast.error(data.error || 'Failed to get auth URL');
        console.log('Auth URL error:', data);
        setConnecting(false);
      }
    } catch (e) {
      toast.error(e.message);
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect AdSense?')) return;
    
    try {
      await fetch('/api/adsense', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'disconnect', userId: user.id })
      });
      toast.success('Disconnected');
      loadData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleFetchDaily = async () => {
    if (!settings?.is_connected) { 
      toast.error('Connect your AdSense account first'); 
      return; 
    }
    
    setFetchingDaily(true);
    const tid = toast.loading('Fetching last 3 days...');
    
    try {
      const res = await fetch('/api/adsense', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'fetch', userId: user.id })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(data.message, { id: tid });
        loadData();
      } else {
        toast.error(data.error || 'Fetch failed', { id: tid });
        console.log('Fetch error:', data);
      }
    } catch (e) {
      toast.error(e.message, { id: tid });
    }
    setFetchingDaily(false);
  };

  const handleFetchUrls = async () => {
    if (!settings?.is_connected) { 
      toast.error('Connect your AdSense account first'); 
      return; 
    }
    
    setFetchingUrls(true);
    const tid = toast.loading('Fetching URL data...');
    
    try {
      const res = await fetch('/api/adsense', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'fetchUrls', userId: user.id })
      });
      
      const data = await res.json();
      console.log('Fetch URLs full response:', JSON.stringify(data, null, 2));
      
      if (data.success) {
        toast.success(data.message || `Fetched ${data.count} URLs`, { id: tid });
      } else {
        const errMsg = data.error || data.message || 'Fetch failed';
        toast.error(errMsg, { id: tid });
        console.error('Fetch URLs error details:', data);
      }
    } catch (e) {
      toast.error(e.message, { id: tid });
      console.error('Fetch URLs exception:', e);
    }
    setFetchingUrls(false);
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await supabase.from('adsense_settings')
        .update({ revenue_share: parseFloat(revenueShare) || 68 })
        .eq('user_id', user.id);
      
      toast.success('Saved!');
      setShowSettings(false);
      loadData();
    } catch (e) { 
      toast.error('Save failed'); 
    }
    setSaving(false);
  };

  const handleManual = async (e) => {
    e.preventDefault();
    if (!manualForm.date || !manualForm.gross_revenue) { 
      toast.error('Date and revenue required'); 
      return; 
    }
    
    setSaving(true);
    const gross = parseFloat(manualForm.gross_revenue) || 0;
    const share = settings?.revenue_share || 68;
    const net = gross * (share / 100);
    const pv = parseInt(manualForm.page_views) || 0;
    const clicks = parseInt(manualForm.clicks) || 0;
    
    try {
      await supabase.from('adsense_daily').upsert({
        user_id: user.id,
        date: manualForm.date,
        gross_revenue: gross,
        net_revenue: net,
        page_views: pv,
        clicks,
        rpm: pv > 0 ? Math.min((net / pv) * 1000, 999999) : 0,
        ctr: pv > 0 ? Math.min((clicks / pv) * 100, 999999) : 0,
        cpc: clicks > 0 ? Math.min(net / clicks, 999999) : 0,
        source: 'manual',
      }, { onConflict: 'user_id,date' });
      
      toast.success('Saved!');
      setShowManual(false);
      setManualForm({ date: new Date().toISOString().split('T')[0], gross_revenue: '', page_views: '', clicks: '' });
      loadData();
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!selectedEntry) return;
    
    try {
      await supabase.from('adsense_daily').delete().eq('id', selectedEntry.id);
      toast.success('Deleted');
      setShowDelete(false);
      setSelectedEntry(null);
      loadData();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  // Calculate totals
  const totalNet = dailyData.reduce((s, d) => s + (Number(d.net_revenue) || 0), 0);
  const totalGross = dailyData.reduce((s, d) => s + (Number(d.gross_revenue) || 0), 0);
  const totalPV = dailyData.reduce((s, d) => s + (Number(d.page_views) || 0), 0);
  const totalClicks = dailyData.reduce((s, d) => s + (Number(d.clicks) || 0), 0);
  const avgRpm = totalPV > 0 ? (totalNet / totalPV) * 1000 : 0;
  const avgCtr = totalPV > 0 ? (totalClicks / totalPV) * 100 : 0;
  const avgCpc = totalClicks > 0 ? totalNet / totalClicks : 0;

  const today = new Date().toISOString().split('T')[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
  const todayData = dailyData.find(d => d.date === today);
  const yesterdayData = dailyData.find(d => d.date === yesterday);
  const monthLabel = getMonthLabel(getViewMonth());
  const isConnected = settings?.is_connected;

  if (loading) return <SkeletonPublisherPlatform />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Google AdSense</h1>
          <p className="text-muted-foreground">Track your AdSense revenue</p>
          {settings?.last_fetch && <LastUpdated value={settings.last_fetch} className="mt-2" />}
        </div>
        <div className="flex gap-2">
          {isConnected && (
            <Link to="/adsense/urls">
              <Button variant="outline">
                <LinkIcon className="h-4 w-4 mr-2" />URL Stats
              </Button>
            </Link>
          )}
          {isConnected && (
            <Button variant="outline" onClick={() => setShowSettings(true)}>
              <Settings className="h-4 w-4 mr-2" />Settings
            </Button>
          )}
        </div>
      </div>

      {/* Connection Status */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {isConnected ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-yellow-500" />
              )}
              <div>
                <p className="font-medium">{isConnected ? 'Connected' : 'Not Connected'}</p>
                {isConnected && (
                  <p className="text-sm text-muted-foreground">
                    {settings.account_name || settings.pub_id} • Share: {settings.revenue_share}%
                    {settings.last_fetch && ` • Last: ${formatDate(settings.last_fetch)}`}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {!isConnected ? (
                <Button onClick={handleConnect} disabled={connecting}>
                  <LogIn className={`h-4 w-4 mr-2 ${connecting ? 'animate-pulse' : ''}`} />
                  {connecting ? 'Connecting...' : 'Sign in with Google'}
                </Button>
              ) : (
                <>
                  <Button size="sm" onClick={handleFetchDaily} disabled={fetchingDaily || fetchingUrls}>
                    <Database className={`h-4 w-4 mr-2 ${fetchingDaily ? 'animate-pulse' : ''}`} />
                    {fetchingDaily ? 'Fetching...' : 'Fetch Daily'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleFetchUrls} disabled={fetchingDaily || fetchingUrls}>
                    <LinkIcon className={`h-4 w-4 mr-2 ${fetchingUrls ? 'animate-pulse' : ''}`} />
                    {fetchingUrls ? 'Fetching...' : 'Fetch URLs'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={handleDisconnect}>
                    <Unplug className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Today</p>
            <p className="text-xl font-bold text-green-600">{formatCurrency(todayData?.net_revenue || 0)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Yesterday</p>
            <p className="text-xl font-bold text-green-600">{formatCurrency(yesterdayData?.net_revenue || 0)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{monthLabel}</p>
            <p className="text-xl font-bold text-green-600">{formatCurrency(totalNet)}</p>
            <p className="text-xs text-muted-foreground">Gross: {formatCurrency(totalGross)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">RPM</p>
            <p className="text-xl font-bold">${avgRpm.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">CPC</p>
            <p className="text-xl font-bold">${avgCpc.toFixed(2)}</p>
          </CardContent>
        </Card>
      </div>

      {/* 30-day earnings trend */}
      {isConnected && (
        <EarningsTrendChart
          userId={user?.id}
          table="adsense_daily"
          label="AdSense net earnings"
          days={30}
        />
      )}

      {/* Daily Performance Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle>Daily Performance</CardTitle>
            <div className="flex items-center gap-2">
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => setMonthOffset(Math.min(2, monthOffset + 1))} 
                disabled={monthOffset >= 2}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium min-w-[130px] text-center">{monthLabel}</span>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => setMonthOffset(Math.max(0, monthOffset - 1))} 
                disabled={monthOffset <= 0}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowManual(true)}>
                <Plus className="h-4 w-4 mr-1" />Add
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Page Views</TableHead>
                    <TableHead className="text-right">Impr</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                    <TableHead className="text-right">CTR</TableHead>
                    <TableHead className="text-right">CPC</TableHead>
                    <TableHead className="text-right">RPM</TableHead>
                    <TableHead className="text-right">Net Revenue</TableHead>
                    <TableHead>Src</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dailyData.length === 0 ? (
                    <TableEmpty colSpan={10} message={`No data for ${monthLabel}`} />
                  ) : (
                    dailyData.map(d => (
                      <TableRow key={d.id}>
                        <TableCell className="font-medium">{formatDate(d.date)}</TableCell>
                        <TableCell className="text-right">{formatNumber(d.page_views)}</TableCell>
                        <TableCell className="text-right">{formatNumber(d.impressions)}</TableCell>
                        <TableCell className="text-right">{formatNumber(d.clicks)}</TableCell>
                        <TableCell className="text-right">{(d.ctr || 0).toFixed(2)}%</TableCell>
                        <TableCell className="text-right">${(d.cpc || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right">${(d.rpm || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right font-medium text-green-600">
                          {formatCurrency(d.net_revenue)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={d.source === 'api' ? 'success' : 'warning'}>{d.source}</Badge>
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            onClick={() => { setSelectedEntry(d); setShowDelete(true); }}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{monthLabel} Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Days</p>
              <p className="text-lg font-medium">{dailyData.length}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Page Views</p>
              <p className="text-lg font-medium">{formatNumber(totalPV)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Clicks</p>
              <p className="text-lg font-medium">{formatNumber(totalClicks)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Avg CTR</p>
              <p className="text-lg font-medium">{avgCtr.toFixed(2)}%</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Revenue Share</p>
              <p className="text-lg font-medium">{settings?.revenue_share || 68}%</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Settings Modal */}
      <Modal isOpen={showSettings} onClose={() => setShowSettings(false)} title="AdSense Settings">
        <form onSubmit={handleSaveSettings}>
          <div className="space-y-4">
            <Input label="Publisher ID" value={settings?.pub_id || ''} disabled />
            <Input label="Account Name" value={settings?.account_name || ''} disabled />
            <Input 
              label="Revenue Share (%)" 
              type="number" 
              min="1" 
              max="100" 
              value={revenueShare} 
              onChange={e => setRevenueShare(e.target.value)} 
            />
          </div>
          <ModalFooter>
            <Button variant="outline" type="button" onClick={() => setShowSettings(false)}>Cancel</Button>
            <Button type="submit" loading={saving}>
              <Save className="h-4 w-4 mr-2" />Save
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Manual Entry Modal */}
      <Modal isOpen={showManual} onClose={() => setShowManual(false)} title="Add Entry">
        <form onSubmit={handleManual}>
          <div className="space-y-4">
            <Input 
              label="Date" 
              type="date" 
              value={manualForm.date} 
              onChange={e => setManualForm({ ...manualForm, date: e.target.value })} 
              required 
            />
            <Input 
              label="Gross Revenue ($)" 
              type="number" 
              step="0.01" 
              value={manualForm.gross_revenue} 
              onChange={e => setManualForm({ ...manualForm, gross_revenue: e.target.value })} 
              required 
            />
            <Input 
              label="Page Views" 
              type="number" 
              value={manualForm.page_views} 
              onChange={e => setManualForm({ ...manualForm, page_views: e.target.value })} 
            />
            <Input 
              label="Clicks" 
              type="number" 
              value={manualForm.clicks} 
              onChange={e => setManualForm({ ...manualForm, clicks: e.target.value })} 
            />
          </div>
          <ModalFooter>
            <Button variant="outline" type="button" onClick={() => setShowManual(false)}>Cancel</Button>
            <Button type="submit" loading={saving}>Save</Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal 
        isOpen={showDelete} 
        onClose={() => { setShowDelete(false); setSelectedEntry(null); }} 
        title="Delete Entry"
      >
        <Alert variant="warning">
          <AlertDescription>
            Delete entry for {selectedEntry && formatDate(selectedEntry.date)}?
          </AlertDescription>
        </Alert>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
          <Button variant="destructive" onClick={handleDelete}>Delete</Button>
        </ModalFooter>
      </Modal>

      {/* HapiGuard banner */}
      <div className="rounded-xl border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/70 px-4 py-3 flex items-center gap-3">
        <ShieldCheck className="h-5 w-5 text-emerald-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">HapiGuard click protection</p>
          <p className="text-xs text-emerald-800 dark:text-emerald-300">Protect your AdSense and Ad Manager revenue from invalid clicks and click bombing in real time.</p>
        </div>
        <Link to="/hapiguard" className="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white transition-colors whitespace-nowrap">
          HapiGuard →
        </Link>
      </div>
    </div>
  );
}
